import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Card from "components/card";
import { MdArrowBack, MdDelete, MdReply } from "react-icons/md";

const LireNotification = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        // In a real application, you would fetch this from your API
        // For now, we'll simulate an API call with dummy data
        setTimeout(() => {
          // Sample notification data - in real app, fetch this from API based on ID
          const dummyNotifications = {
            1: {
              id: 1,
              title: "Nouvelle demande de contact",
              content: `Bonjour,

Un utilisateur a demandé à vous contacter concernant son article "Les avancées technologiques en 2025".

L'utilisateur a laissé les informations suivantes:
- Nom: Jean Dupont
- Email: jean.dupont@example.com
- Message: J'aimerais discuter de possibles corrections à apporter à mon article avant sa publication finale.

Veuillez le contacter dès que possible.

Cordialement,
L'équipe de gestion`,
              sender: "Système",
              sender_email: "system@example.com",
              date: "2025-05-07T14:32:00",
              read: false,
              attachments: [],
            },
            2: {
              id: 2,
              title: "Votre projet a été approuvé",
              content: `Félicitations !

Votre projet "Analyse comparative des méthodes d'apprentissage automatique" a été approuvé par l'administrateur.

Vous pouvez maintenant passer à l'étape suivante de la publication.

Nous vous remercions pour votre contribution.

Bien cordialement,
L'équipe administrative`,
              sender: "Admin",
              sender_email: "admin@example.com",
              date: "2025-05-06T09:15:00",
              read: false,
              attachments: [
                { name: "approbation.pdf", size: "245 KB", url: "#" },
              ],
            },
            3: {
              id: 3,
              title: "Rappel: Réunion à 14h00",
              content: `Ce message est un rappel pour la réunion d'équipe qui aura lieu aujourd'hui à 14h00.

Ordre du jour:
1. Point sur les articles en cours
2. Planification des prochaines publications
3. Questions diverses

Lieu: Salle de conférence virtuelle
Lien: https://meeting.example.com/team123

N'oubliez pas de préparer vos rapports hebdomadaires.

Cordialement,
Le secrétariat`,
              sender: "Système",
              sender_email: "system@example.com",
              date: "2025-05-05T08:00:00",
              read: true,
              attachments: [
                { name: "ordre_du_jour.docx", size: "132 KB", url: "#" },
                { name: "rapports_precedents.xlsx", size: "456 KB", url: "#" },
              ],
            },
          };

          const foundNotification = dummyNotifications[id];

          if (foundNotification) {
            // Mark as read
            foundNotification.read = true;
            setNotification(foundNotification);
            setLoading(false);
          } else {
            setError("Notification non trouvée");
            setLoading(false);
          }
        }, 800); // Simulate network delay
      } catch (err) {
        setError(err.message || "Une erreur est survenue");
        setLoading(false);
      }
    };

    fetchNotification();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} à ${date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const handleDelete = () => {
    // In a real app, you would delete the notification via API
    alert("Fonctionnalité de suppression à implémenter");
    navigate("/admin/notifications");
  };

  const handleReply = () => {
    // In a real app, you would implement reply functionality
    alert("Fonctionnalité de réponse à implémenter");
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500"></div>
      </div>
    );
  }

  if (error || !notification) {
    return (
      <Card extra={"w-full h-full p-4"}>
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-xl font-bold text-red-500">
            {error || "Notification non trouvée"}
          </div>
          <Link
            to="/admin/notifications"
            className="mt-4 flex items-center rounded-full bg-navy-700 px-4 py-2 text-sm text-white hover:bg-navy-800"
          >
            <MdArrowBack className="mr-2" /> Retour aux notifications
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="mt-3 grid grid-cols-1 gap-5">
      <Card extra={"w-full h-full p-4"}>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/admin/notifications"
              className="mr-4 flex items-center text-navy-700 hover:text-gray-600 dark:text-white dark:hover:text-gray-300"
            >
              <MdArrowBack className="h-5 w-5" />
              <span className="ml-1">Retour</span>
            </Link>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {notification.title}
            </h4>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReply}
              className="flex items-center rounded-full bg-blue-500 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-600"
            >
              <MdReply className="mr-1 h-4 w-4" /> Répondre
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center rounded-full bg-red-100 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
            >
              <MdDelete className="mr-1 h-4 w-4" /> Supprimer
            </button>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between border-b border-gray-200 pb-4 dark:border-gray-700">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              De: <span className="font-medium">{notification.sender}</span>
              {notification.sender_email && ` <${notification.sender_email}>`}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Reçu le: {formatDate(notification.date)}
            </p>
          </div>
        </div>

        <div className="mb-6 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
          {notification.content}
        </div>

        {notification.attachments && notification.attachments.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Pièces jointes ({notification.attachments.length})
            </p>
            <div className="flex flex-wrap gap-3">
              {notification.attachments.map((attachment, index) => (
                <a
                  key={index}
                  href={attachment.url}
                  className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <div className="mr-2 h-8 w-8 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {attachment.name}
                    </p>
                    <p className="text-xs text-gray-500">{attachment.size}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
          <Link
            to="/admin/notifications"
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <MdArrowBack className="mr-1 h-4 w-4" /> Retour à toutes les
            notifications
          </Link>
          <div className="flex gap-2">
            <button
              onClick={handleReply}
              className="flex items-center rounded-full bg-blue-500 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-600"
            >
              <MdReply className="mr-1 h-4 w-4" /> Répondre
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center rounded-full bg-red-100 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
            >
              <MdDelete className="mr-1 h-4 w-4" /> Supprimer
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LireNotification;
